import { useRef, useEffect, useCallback } from "react";
import { dds } from "../dds/ddsClient";

const SERVER_TIME_QUERY = `query { serverTime }`;
const RESYNC_INTERVAL_MS = 30000;

/**
 * OPUS-1172: keep a server/client clock offset so the match clock is anchored to
 * DDS server time, never the operator's (possibly skewed) system clock — the
 * role the broker's `brokerRefDate` used to play. Polls DDS `serverTime` on
 * mount and every 30s, corrects for round-trip latency, and returns a stable
 * `getServerNow()` = `Date.now() + offset`.
 *
 * The local interval in the clock only advances the tick *rate*; absolute match
 * time is always computed against this server-anchored now, so the displayed
 * clock can't drift with a wrong workstation clock.
 */
export const useServerClock = () => {
  // 0 until the first successful sync — getServerNow() then just returns the
  // local clock, which is the existing (pre-1172) behaviour, so we never block
  // the clock waiting on the network.
  const offsetRef = useRef(0);

  useEffect(() => {
    let cancelled = false;

    const sync = async () => {
      const t0 = Date.now();
      try {
        const data = await dds.http.request({ query: SERVER_TIME_QUERY });
        const t1 = Date.now();
        const serverMs = Date.parse(data?.serverTime);
        if (cancelled || Number.isNaN(serverMs)) return;
        // Assume the response reflects server time at ~mid-flight so the offset
        // isn't biased by the request's round-trip.
        const clientMidMs = t0 + (t1 - t0) / 2;
        offsetRef.current = serverMs - clientMidMs;
      } catch {
        // Network hiccup — keep the last known offset (clocks drift slowly).
      }
    };

    sync();
    const id = setInterval(sync, RESYNC_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  // Stable identity so the clock's tick effect doesn't re-create every render.
  return useCallback(() => Date.now() + offsetRef.current, []);
};
